import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PopupService {
  private popupVisibilitySubject = new BehaviorSubject<boolean>(false);
  public popupVisibility$ = this.popupVisibilitySubject.asObservable();

  private messageSubject = new BehaviorSubject<string>('');
  public message$ = this.messageSubject.asObservable();

  private iconClassSubject = new BehaviorSubject<string>('');
  public iconClass$ = this.iconClassSubject.asObservable();


  private confirmVisibilitySubject = new BehaviorSubject<boolean>(false);
  public confirmVisibility$ = this.confirmVisibilitySubject.asObservable();

  private confirmActionsSubject = new BehaviorSubject<{ yes?: () => void; no?: () => void }>({});
  public confirmActions$ = this.confirmActionsSubject.asObservable();

  private autoHideTimeout: any = 3000; // Store the timeout reference

  // ---Ward Menu State (Nurse Ward / Patient Worklist sidebar) Start---//

  private activeTabSubject = new BehaviorSubject<string>('vitals');
  public activeTab$ = this.activeTabSubject.asObservable();

  private patientListVisibleSubject = new BehaviorSubject<boolean>(true);
  public patientListVisible$ = this.patientListVisibleSubject.asObservable();

  private sidebarCollapsedSubject = new BehaviorSubject<boolean>(false);
  public sidebarCollapsed$ = this.sidebarCollapsedSubject.asObservable();

  public setActiveTab(tab: string): void {
    this.activeTabSubject.next(tab);
  }

  public togglePatientList(): void {
    const visible = !this.patientListVisibleSubject.value;
    this.patientListVisibleSubject.next(visible);
    // Showing the patient list again means there's no menu rail left to stay collapsed.
    if (visible) {
      this.sidebarCollapsedSubject.next(false);
    }
  }

  public toggleSidebarCollapse(): void {
    const collapsed = !this.sidebarCollapsedSubject.value;
    this.sidebarCollapsedSubject.next(collapsed);
    // Collapsing the whole rail implies the patient list can't be shown either.
    if (collapsed) {
      this.patientListVisibleSubject.next(false);
    }
  }

  // Restores the sidebar to its default state — called each time a new ward opens.
  public resetWardMenuState(): void {
    this.patientListVisibleSubject.next(true);
    this.sidebarCollapsedSubject.next(false);
    this.activeTabSubject.next('vitals');
  }

  // ---Ward Menu State End---//

  constructor() {}

  // Generic method to show the popup
  private showPopup(message: string, iconClass: string, duration: number = 3000): void {
    // Update the BehaviorSubjects
    this.messageSubject.next(message);       // Set the popup message
    this.iconClassSubject.next(iconClass);   // Set the popup icon
    this.popupVisibilitySubject.next(true);  // Make the popup visible

    // Clear any existing timeout to avoid conflicts
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }

    // Automatically hide the popup after the specified duration
    this.autoHideTimeout = setTimeout(() => {
      this.hidePopup();
    }, duration);
  }

  // Success notification
  public showSuccess(message: string, duration?: number): void {
    this.showPopup(message, 'fa fa-check', duration); // Font Awesome icon class for success
  }

  // Danger notification
  public showDanger(message: string, duration?: number): void {
    this.showPopup(message, 'fa fa-exclamation-triangle', duration); // Font Awesome icon class for danger
  }

  // Info notification
  public showInfo(message: string, duration?: number): void {
    this.showPopup(message, 'fa fa-info-circle', duration); // Font Awesome icon class for info
  }

  // Warning notification
  public showWarning(message: string, duration?: number): void {
    this.showPopup(message, 'fa fa-exclamation-circle', duration); // Font Awesome icon class for warning
  }

  // Neutral or generic notification
  public showNeutral(message: string, duration?: number): void {
    this.showPopup(message, 'fa fa-question-circle', duration); // Font Awesome icon class for neutral
  }

  // Method to hide the popup
  public hidePopup(): void {
    this.popupVisibilitySubject.next(false); // Hide the popup
  }


  public showConfirmPopup(message: string, yesAction: () => void, noAction: () => void): void {
    this.messageSubject.next(message); // Set the popup message
    this.confirmVisibilitySubject.next(true); // Show the popup
    this.confirmActionsSubject.next({ yes: yesAction, no: noAction }); // Set actions for buttons
  }

  // Method to hide the popup
  public hideConfirmPopup(): void {
    this.confirmVisibilitySubject.next(false); // Hide the popup
    this.confirmActionsSubject.next({}); // Clear actions
  }
}
